import { Component, ChangeDetectionStrategy, inject } from '@angular/core';

import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { fadeIn } from '../@commons/animations/animations';
import { SharedModule } from '../@commons/ui-components/shared.module';
import {
  HeaderPaddingSafeAreaComponent
} from '../@commons/@components/header-padding-safe-area/header-padding-safe-area.component';
import RoutesConstants from '../@commons/constants/routes.constants';
import { PlusIconComponent } from '../@icons/plus-icon.component';
import { GardenActionsToastComponent } from './@toasts/garden-actions-toast/garden-actions-toast.component';
import { SeedActionsToastComponent } from './@toasts/seed-actions-toast/seed-actions-toast.component';
import { SeedApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/seed/seed-api';
import {
  SeedListRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/location/seed-list.request';
import { StorageService } from '../@commons/@services/front/storage.service';
import { GardenStore } from '../@commons/@services/data/garden.store';
import { TabComponent } from '../@commons/ui-components/tab/tab.component';



@Component({
  selector: 'vw-garden', // Indicates that this component is standalone
  imports: [SharedModule, RouterOutlet, TranslatePipe, HeaderPaddingSafeAreaComponent, PlusIconComponent, GardenActionsToastComponent, SeedActionsToastComponent], // Required modules and components
  templateUrl: './garden.component.html', // Path to the component's HTML template
  styleUrls: ['./garden.component.scss'], // Path to the component's SCSS styles
  providers: [{
    provide: SeedApi,
    useClass: SeedApi
  }],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [fadeIn]
})
export class GardenComponent {
  activeTab = 0; // Default tab index (0 for the first tab)
  /** Nombre de cultures, `null` tant qu'il n'est pas connu : « Plantations » sans chiffre (145). */
  cultivationCounts: number | null = null;
  isLoading = true;
  showAddDropdown = false;
  showActionsToast = false;
  /** Nombre de graines, dans le titre de l'onglet « Mes graines (5) ». */
  seedsCount = 0;
  /** Feuille « Ajouter des graines », ouverte par le « + » sur l'onglet Mes graines. */
  showSeedActions = false;

  private readonly gardenStore = inject(GardenStore);

  constructor(private route: ActivatedRoute,
    private router: Router,
    private seedApi: SeedApi,
    private storageService: StorageService) { } // Constructor to inject the TaskService

  /**
   * Array containing data for the tabs.
   * Each object represents a tab with its label, icon, and styling classes.
   * @type {Array<{ label: string; icon: string; tailwindClasses: string; width: string; border: string; }>}
   */
  tabData: TabComponent['tabs'] = [];

  setTabData() {
    this.tabData = [
      {
        // En panne, l'onglet ne dit pas « Plantations (0) » : ce serait annoncer un potager vide.
        label: this.cultivationCounts === null ? 'garden.tabs.cultivations.tab_title_unknown' : 'garden.tabs.cultivations.tab_title',
        labelPayload: { cultivationsCount: this.cultivationCounts },
        icon: 'fas fa-home', tailwindClasses: 'text-sm', width: '100%', border: 'rounded-full'
      },
      { label: 'garden.tabs.seeds.tab_title', labelPayload: { seedsCount: this.seedsCount }, icon: 'fas fa-search', tailwindClasses: 'text-sm', width: '100%', border: 'rounded-full' },
    ];
  }



  ngOnInit() {
    // Mon potager lit les mêmes cultures au même moment : une seule requête.
    this.gardenStore
      .myGarden()
      .then((response) => {
        this.cultivationCounts = response.length;
      })
      .catch(() => {
        this.cultivationCounts = null;
      })
      .finally(() => {
        this.setTabData();
        this.isLoading = false;
      });
    this.loadSeedsCount();
    this.route.firstChild?.url.subscribe((urlSegments) => {
      const routePath = urlSegments[0]?.path;
      if (routePath === 'list') {
        this.activeTab = 0;
      } else if (routePath === 'seeds') {
        this.activeTab = 1;
      }
    });
  }

  /**
   * Le total vient de la pagination : une page d'une seule graine suffit.
   * En panne, l'onglet garde 0 sans bloquer l'écran.
   */
  private async loadSeedsCount() {
    try {
      const page = await this.seedApi.list(new SeedListRequest(), 1, 1, this.storageService.getCurrentLanguage());
      this.seedsCount = page?.page?.totalRecords ?? 0;
    } catch {
      this.seedsCount = 0;
    }
    if (this.tabData.length) this.setTabData();
  }

  /**
   * Le « + » suit l'onglet : sur Mes graines il ajoute des graines, ailleurs
   * il propose planche, plante, historique et zone.
   */
  onPlusClicked() {
    if (this.activeTab === 1) {
      this.showSeedActions = true;
    } else {
      this.showActionsToast = true;
    }
  }

  /**
 * Method to handle tab selection.
 * Updates the activeTab index based on the selected tab index.
 * @param {number} index - The index of the selected tab.
 */
  onTabSelected(index: number) {
    this.activeTab = index; // Set the active tab to the selected index
    // Navigate to the appropriate route based on the selected tab index
    if (index === 0) {
      this.router.navigate([RoutesConstants.GARDEN_LIST]);
    } else if (index === 1) {
      this.router.navigate([RoutesConstants.GARDEN_SEEDS]);
    }
  }

  onAddPlant() {
    this.router.navigate([RoutesConstants.GARDEN_ADDPLANT]);
  }

  onAddSeed() {
    this.router.navigate([RoutesConstants.USER_ADD_SEED_SELECT]);
  }
}
